import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, DollarSign, TrendingUp, Clock, Plus,
  ChevronUp, Target, Zap,
} from 'lucide-react';

// ─── Live Register ──────────────────────────────────────────────────
// When you're broke, you count every dollar. This is your shift
// earnings in real-time — not points, not percentages — dollars.
// Like a cash register that only shows YOUR cut.

interface EarningEvent {
  id: number;
  time: string;
  description: string;
  amount: number;
  type: 'base_tip' | 'upsell_bonus' | 'drink_bonus';
  table: string;
}

const SHIFT_EVENTS: EarningEvent[] = [
  { id: 1, time: '5:12 PM', description: 'Table 4 — opened and served', amount: 12.00, type: 'base_tip', table: '4' },
  { id: 2, time: '5:18 PM', description: 'Wagyu upsell landed', amount: 6.80, type: 'upsell_bonus', table: '4' },
  { id: 3, time: '5:22 PM', description: '2x Mango Lassi sold', amount: 3.20, type: 'drink_bonus', table: '4' },
  { id: 4, time: '5:35 PM', description: 'Table 7 — opened and served', amount: 18.50, type: 'base_tip', table: '7' },
  { id: 5, time: '5:41 PM', description: 'Döner upgrade accepted', amount: 1.60, type: 'upsell_bonus', table: '7' },
  { id: 6, time: '5:58 PM', description: 'Table 2 — opened and served', amount: 22.00, type: 'base_tip', table: '2' },
  { id: 7, time: '6:04 PM', description: 'Wagyu upsell landed', amount: 6.80, type: 'upsell_bonus', table: '2' },
  { id: 8, time: '6:07 PM', description: '3x Mango Lassi sold', amount: 4.80, type: 'drink_bonus', table: '2' },
  { id: 9, time: '6:10 PM', description: 'Dubai Chocolate — "for the table"', amount: 2.80, type: 'upsell_bonus', table: '2' },
  { id: 10, time: '6:25 PM', description: 'Table 5 — opened and served', amount: 15.00, type: 'base_tip', table: '5' },
  { id: 11, time: '6:42 PM', description: 'Table 9 — opened and served', amount: 28.00, type: 'base_tip', table: '9' },
  { id: 12, time: '6:48 PM', description: 'Wagyu upsell — table of 4!', amount: 13.60, type: 'upsell_bonus', table: '9' },
  { id: 13, time: '6:52 PM', description: '4x drinks sold', amount: 6.40, type: 'drink_bonus', table: '9' },
  { id: 14, time: '7:05 PM', description: 'Table 3 — opened and served', amount: 19.00, type: 'base_tip', table: '3' },
  { id: 15, time: '7:15 PM', description: 'Döner upgrade accepted', amount: 1.60, type: 'upsell_bonus', table: '3' },
  { id: 16, time: '7:18 PM', description: 'Dubai Chocolate landed', amount: 2.80, type: 'upsell_bonus', table: '3' },
];

const LiveRegister: React.FC = () => {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(8);

  // Simulate events trickling in
  useEffect(() => {
    if (visibleCount < SHIFT_EVENTS.length) {
      const timer = setTimeout(() => setVisibleCount(c => c + 1), 3000);
      return () => clearTimeout(timer);
    }
  }, [visibleCount]);

  const visible = SHIFT_EVENTS.slice(0, visibleCount);
  const total = visible.reduce((s, e) => s + e.amount, 0);
  const baseTips = visible.filter(e => e.type === 'base_tip').reduce((s, e) => s + e.amount, 0);
  const upsellExtra = visible.filter(e => e.type !== 'base_tip').reduce((s, e) => s + e.amount, 0);
  const hourlyRate = total / 2.5; // ~2.5 hours into shift
  const shiftProjection = hourlyRate * 5.5; // 5.5 hour shift

  // What the money means
  const getMeaning = (amount: number): string => {
    if (amount >= 180) return 'That\'s rent day covered.';
    if (amount >= 140) return 'That\'s a full week of groceries.';
    if (amount >= 100) return 'That\'s your phone bill handled.';
    if (amount >= 60) return 'That\'s three days of meals.';
    if (amount >= 30) return 'That\'s tomorrow\'s food sorted.';
    return 'Keep going — every dollar counts.';
  };

  const typeConfig = {
    base_tip: { label: 'Tip', color: 'text-gray-400', bg: 'bg-gray-100' },
    upsell_bonus: { label: 'Upsell', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    drink_bonus: { label: 'Drink', color: 'text-blue-600', bg: 'bg-blue-50' },
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sticky earnings header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 pt-3">
            <button onClick={() => navigate('/restaurant/mockups')} className="text-gray-400 hover:text-gray-600 transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-gray-400">Live Register</span>
            <div className="flex-1" />
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] text-gray-400">LIVE</span>
            </div>
          </div>

          <div className="py-4">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-xs text-gray-500 mb-0.5">You've made tonight</div>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-gray-900">${total.toFixed(2)}</span>
                  {visibleCount > 1 && (
                    <span className="text-sm text-emerald-600 font-medium flex items-center ml-2">
                      <ChevronUp className="w-3.5 h-3.5" />
                      ${visible[visible.length - 1].amount.toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">{getMeaning(total)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500">On pace for</div>
                <div className="text-xl font-bold text-gray-700">${shiftProjection.toFixed(0)}</div>
                <div className="text-[10px] text-gray-400">by end of shift</div>
              </div>
            </div>

            {/* The split */}
            <div className="mt-3 flex items-center gap-2">
              <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden flex">
                <div className="bg-gray-300 rounded-l-full" style={{ width: `${(baseTips / total) * 100}%` }} />
                <div className="bg-emerald-400" style={{ width: `${(upsellExtra / total) * 100}%` }} />
              </div>
              <span className="text-xs text-emerald-600 font-semibold whitespace-nowrap">
                +${upsellExtra.toFixed(2)} from upsells
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        {/* Context card */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs text-amber-800">
            <span className="font-semibold">Without upsells tonight:</span> ~${baseTips.toFixed(0)} so far.
            <span className="font-semibold"> With your upsells:</span> ${total.toFixed(0)}.
            That extra <span className="font-bold text-emerald-700">${upsellExtra.toFixed(0)}</span> is because you spoke up. Keep going.
          </p>
        </div>

        {/* Event feed */}
        <div className="space-y-1">
          {visible.map((event, idx) => {
            const tc = typeConfig[event.type];
            const isNew = idx === visibleCount - 1;
            return (
              <div
                key={event.id}
                className={`flex items-center gap-3 bg-white rounded-lg border px-4 py-3 transition-all duration-500 ${
                  isNew ? 'border-emerald-200 shadow-sm' : 'border-gray-100'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  event.type === 'base_tip' ? 'bg-gray-100' : 'bg-emerald-50'
                }`}>
                  {event.type === 'base_tip' ? (
                    <DollarSign className="w-4 h-4 text-gray-400" />
                  ) : (
                    <Plus className="w-4 h-4 text-emerald-500" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900">{event.description}</div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span>{event.time}</span>
                    <span className={`${tc.color} ${tc.bg} px-1.5 py-0.5 rounded text-[10px] font-medium`}>{tc.label}</span>
                  </div>
                </div>

                <div className={`text-sm font-bold flex-shrink-0 ${
                  event.type === 'base_tip' ? 'text-gray-600' : 'text-emerald-600'
                }`}>
                  +${event.amount.toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Milestone markers */}
        {total >= 50 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Milestone</div>
            {total >= 150 ? (
              <p className="text-sm text-gray-700">You've passed <span className="font-bold text-emerald-600">$150</span>. Phone bill? Done.</p>
            ) : total >= 100 ? (
              <p className="text-sm text-gray-700">You've passed <span className="font-bold text-emerald-600">$100</span>. This week's groceries are covered.</p>
            ) : (
              <p className="text-sm text-gray-700">You've passed <span className="font-bold text-emerald-600">$50</span>. Tomorrow's meals are handled.</p>
            )}
          </div>
        )}

        {/* Still going */}
        {visibleCount < SHIFT_EVENTS.length && (
          <div className="text-center py-4 text-xs text-gray-400 animate-pulse">
            Listening for your next table...
          </div>
        )}

        {visibleCount >= SHIFT_EVENTS.length && (
          <div className="bg-gray-900 rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-white mb-1">${total.toFixed(2)}</div>
            <p className="text-sm text-gray-400 mb-3">
              Tonight so far. <span className="text-emerald-400 font-medium">${upsellExtra.toFixed(2)}</span> of that
              is money you made by speaking up.
            </p>
            <p className="text-xs text-gray-600">
              That's not points. That's not a score. That's cash in your pocket tonight.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveRegister;
